import { Link } from "react-router-dom";
import { ProfileDropdown } from "./ProfileDropdown";
import { Menu, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SidebarNav } from "./SidebarNav";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const Navigation = () => {
  const { isAuthenticated, session } = useAuth();
  // For demo purposes, we'll hardcode the credits. In a real app, this would come from your backend
  const maxCredits = 1000;
  const remainingCredits = 750;
  const progressPercentage = (remainingCredits / maxCredits) * 100;

  const { data: profile } = useQuery({
    queryKey: ["profile", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("organization, organization_icon")
        .eq("id", session.user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
  });

  console.log("Profile data:", profile); // Debug log to check profile data

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center justify-between">
        <div className="flex items-center gap-4">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64">
              <SidebarNav />
            </SheetContent>
          </Sheet>
          <Link to="/" className="flex items-center space-x-2">
            <span className="font-bold">Crucar</span>
            <sup className="text-xs font-medium text-muted-foreground">
              <Sparkles className="h-4 w-4 text-blue-500 inline-block mr-1" />
              beta
            </sup>
          </Link>
        </div>
        {isAuthenticated && (
          <div className="flex items-center gap-4">
            {profile?.organization && (
              <div className="hidden md:flex items-center gap-2">
                {profile.organization_icon && (
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={profile.organization_icon} alt={profile.organization} />
                    <AvatarFallback>{profile.organization.charAt(0)}</AvatarFallback>
                  </Avatar>
                )}
                <span className="text-sm font-medium">{profile.organization}</span>
              </div>
            )}
            <div className="hidden md:flex items-center space-x-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Progress value={progressPercentage} className="w-16" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">{remainingCredits} credits left</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <ProfileDropdown />
          </div>
        )}
      </div>
    </nav>
  );
};